import api from "./api";

// Dashboard service
// Uses role-based API endpoints according to DASHBOARD_API.md

const normalizeStats = (dto = {}) => ({
  companies:
    Number(dto.companies ?? dto.companyCount ?? dto.total_companies ?? 0) || 0,
  processes:
    Number(dto.processes ?? dto.processCount ?? dto.total_processes ?? 0) || 0,
  tasks: Number(dto.tasks ?? dto.taskCount ?? dto.total_tasks ?? 0) || 0,
  jobs: Number(dto.jobs ?? dto.jobCount ?? dto.total_jobs ?? 0) || 0,
});

// GET /dashboard - For SUPERADMIN to get total system counts
export async function getDashboardCounts(companyId = null) {
  try {
    const endpoint =
      companyId ?
        `/dashboard?companyId=${encodeURIComponent(companyId)}`
      : "/dashboard";
    const res = await api.get(endpoint);
    if (res && typeof res === "object") return normalizeStats(res);
    return { companies: 0, processes: 0, tasks: 0, jobs: 0 };
  } catch (e) {
    console.error("Failed to fetch dashboard counts", e);
    throw e;
  }
}

// POST /hierarchical-view/relation/dashboard/bulk - For ADMIN and normal users
export async function getDashboardCountsByUsers(
  userIds = [],
  companyId = null,
) {
  try {
    const body = { userIds: Array.isArray(userIds) ? userIds : [userIds] };
    if (companyId != null) {
      body.companyId = Number(companyId);
    }
    const res = await api.post(
      "/hierarchical-view/relation/dashboard/bulk",
      body,
    );

    if (!res || !Array.isArray(res.results)) {
      return { companies: 0, processes: 0, tasks: 0, jobs: 0 };
    }

    // Aggregate counts from all user results
    let totalCounts = {
      companies: 0,
      processes: 0,
      tasks: 0,
      jobs: 0,
    };

    res.results.forEach((userResult) => {
      if (userResult.success && userResult.data) {
        const counts = userResult.data.counts || {};
        // For now, just take the max counts (or you could aggregate differently)
        totalCounts.companies = Math.max(
          totalCounts.companies,
          counts.companies || 0,
        );
        totalCounts.processes = Math.max(
          totalCounts.processes,
          counts.processes || 0,
        );
        totalCounts.tasks = Math.max(totalCounts.tasks, counts.tasks || 0);
        totalCounts.jobs = Math.max(totalCounts.jobs, counts.jobs || 0);
      }
    });

    return totalCounts;
  } catch (e) {
    console.error("Failed to fetch dashboard counts by users", e);
    throw e;
  }
}

// Upload an Excel file to import dashboard-related data.
// Accepts File or Blob; wraps it in FormData and tries a few common endpoints.
export async function importDashboardExcel(file) {
  if (!file) throw new Error("No file provided");
  const form = new FormData();
  form.append("file", file);

  const tryEndpoints = ["/import/excel"];
  let lastErr = null;
  for (const ep of tryEndpoints) {
    try {
      // Use the lower-level request to allow FormData without JSON headers
      const res = await api.request(ep, { method: "POST", body: form });
      return res;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("Failed to import file");
}

const normalizeDashboardBuilding = (dto = {}) => {
  const rawCountry = dto.country;
  const countryIso3 =
    rawCountry?.isoCode3 ||
    rawCountry?.iso_code3 ||
    rawCountry?.iso3 ||
    rawCountry?.code3 ||
    rawCountry?.country_iso3 ||
    (typeof rawCountry === "string" ? rawCountry : "");

  const countryName =
    rawCountry?.countryName ||
    rawCountry?.country_name ||
    rawCountry?.name ||
    (typeof rawCountry === "string" ? rawCountry : "");

  const rawCity = dto.city;
  const cityName =
    rawCity?.cityName ||
    rawCity?.city_name ||
    rawCity?.name ||
    (typeof rawCity === "string" ? rawCity : "");

  return {
    building_id: dto.building_id ?? dto.id ?? null,
    name: dto.name || dto.building_name || "",
    country: rawCountry ?? null,
    city: rawCity ?? null,
    countryIso3: String(countryIso3 || "").toUpperCase(),
    countryName: String(countryName || ""),
    cityName: String(cityName || ""),
  };
};

export async function getDashboardBuildingInformation() {
  const res = await api.get("/dashboard/building/information");
  const list =
    Array.isArray(res) ? res
    : Array.isArray(res?.data) ? res.data
    : [];
  return list.map(normalizeDashboardBuilding);
}
